// Nav — real swap execution against Uniswap V3 SwapRouter02 on Robinhood Chain 4663.
// No web3 lib. Direct EIP-1193 (window.ethereum) + raw calldata.

export const CHAIN_ID     = 4663;
export const CHAIN_HEX    = "0x1237";
export const SWAP_ROUTER  = "0xcaf681a66d020601342297493863e78c959e5cb2"; // SwapRouter02
export const USDG         = "0x5fc5360D0400a0Fd4f2af552ADD042D716F1d168";
export const WETH         = "0x0Bd7D308f8E1639FAb988df18A8011f41EAcAD73";
export const USDG_DEC     = 6;
export const WETH_DEC     = 18;

// Selectors
const SEL_APPROVE          = "0x095ea7b3";
const SEL_ALLOWANCE        = "0xdd62ed3e";
const SEL_BAL              = "0x70a08231";
const SEL_EXACT_IN_SINGLE  = "0x04e45aaf";
const SEL_EXACT_IN         = "0xb858183f";  // exactInput((bytes,address,uint256,uint256))

// WETH/USDG default fee tier (0.3%) — most liquid on chain 4663
export const WETH_USDG_FEE = 3000;

const RPC_FALLBACK = process.env.REACT_APP_RH_CHAIN_RPC || "https://rpc.mainnet.chain.robinhood.com";

function encAddress(a) {
  return a.toLowerCase().replace(/^0x/, "").padStart(64, "0");
}
function encUint(n) {
  const bi = typeof n === "bigint" ? n : BigInt(n);
  return bi.toString(16).padStart(64, "0");
}

async function ethRequest(method, params) {
  return await window.ethereum.request({ method, params });
}

async function readRpc(method, params) {
  try {
    if (window.ethereum) return await ethRequest(method, params);
  } catch {}
  const r = await fetch(RPC_FALLBACK, {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ jsonrpc: "2.0", id: 1, method, params }),
  });
  const j = await r.json();
  if (j.error) throw new Error(j.error.message);
  return j.result;
}

export async function readAllowance(token, owner, spender) {
  const data = SEL_ALLOWANCE + encAddress(owner) + encAddress(spender);
  const res = await readRpc("eth_call", [{ to: token, data }, "latest"]);
  return BigInt(res);
}

export async function readDecimals(token) {
  const res = await readRpc("eth_call", [{ to: token, data: "0x313ce567" }, "latest"]);
  return Number(BigInt(res));
}

export async function readBalance(token, owner) {
  const data = SEL_BAL + encAddress(owner);
  const res = await readRpc("eth_call", [{ to: token, data }, "latest"]);
  return BigInt(res);
}

/** Send an approve tx. Returns the tx hash. */
export async function sendApprove(token, spender, from, amount) {
  const data = "0x" + SEL_APPROVE.slice(2) + encAddress(spender) + encUint(amount);
  return await ethRequest("eth_sendTransaction", [
    { from, to: token, data },
  ]);
}

/**
 * Send an exactInputSingle swap via SwapRouter02.
 * If `valueEth` is provided, sends msg.value = valueEth (in wei) — used when
 * paying with native ETH (tokenIn should be WETH; SwapRouter02 auto-wraps).
 * Returns the tx hash.
 */
export async function sendSwap({
  from, tokenIn, tokenOut, fee, amountIn, amountOutMin, valueEth = 0n,
}) {
  const params =
    encAddress(tokenIn)
    + encAddress(tokenOut)
    + encUint(fee)
    + encAddress(from)      // recipient
    + encUint(amountIn)
    + encUint(amountOutMin)
    + encUint(0);           // sqrtPriceLimitX96 = 0 (no limit)
  const data = SEL_EXACT_IN_SINGLE + params;
  const tx = { from, to: SWAP_ROUTER, data };
  if (valueEth && valueEth > 0n) tx.value = "0x" + valueEth.toString(16);
  else tx.value = "0x0";
  return await ethRequest("eth_sendTransaction", [tx]);
}

/** Poll for tx receipt. */
export async function waitForReceipt(txHash, { timeoutMs = 90000, intervalMs = 2000 } = {}) {
  const start = Date.now();
  while (Date.now() - start < timeoutMs) {
    try {
      const r = await readRpc("eth_getTransactionReceipt", [txHash]);
      if (r) return r;
    } catch {}
    await new Promise((res) => setTimeout(res, intervalMs));
  }
  throw new Error("Receipt timeout");
}

/** Ensure user is on chain 4663. */
export async function ensureChain() {
  const cid = await ethRequest("eth_chainId", []);
  if (cid !== CHAIN_HEX) {
    try {
      await ethRequest("wallet_switchEthereumChain", [{ chainId: CHAIN_HEX }]);
    } catch (e) {
      throw new Error("Please switch wallet to Robinhood Chain 4663");
    }
  }
}

export const MAX_UINT256 = (BigInt(1) << BigInt(256)) - BigInt(1);

/**
 * Multi-hop exactInput swap. `path` is packed (token+fee+token+fee+token…).
 * If `valueEth` > 0 the tx sends msg.value (SwapRouter02 wraps ETH→WETH when
 * the first token in the path is WETH).
 */
export async function sendSwapMultihop({ from, path, amountIn, amountOutMin, valueEth = 0n }) {
  // ABI-encode struct: bytes offset (0x80), recipient, amountIn, amountOutMinimum, path-length, path-bytes-padded
  const pathHex = path.startsWith("0x") ? path.slice(2) : path;
  const pathBytes = pathHex.length / 2;
  const paddedPath = pathHex + "0".repeat((64 - (pathHex.length % 64)) % 64);
  const params =
    encUint(0x80)                    // offset to path bytes
    + encAddress(from)               // recipient
    + encUint(amountIn)
    + encUint(amountOutMin)
    + encUint(pathBytes)             // length of path
    + paddedPath;
  const data = SEL_EXACT_IN + params;
  const tx = { from, to: SWAP_ROUTER, data };
  if (valueEth && valueEth > 0n) tx.value = "0x" + valueEth.toString(16);
  else tx.value = "0x0";
  return await ethRequest("eth_sendTransaction", [tx]);
}

/** Pack a Uniswap V3 path: [token0, fee0, token1, fee1, token2, …]. */
export function encodePath(tokens, fees) {
  if (tokens.length !== fees.length + 1) throw new Error("path length mismatch");
  let p = "0x";
  for (let i = 0; i < tokens.length; i++) {
    p += tokens[i].toLowerCase().replace(/^0x/, "").padStart(40, "0");
    if (i < fees.length) p += fees[i].toString(16).padStart(6, "0");
  }
  return p;
}
